import { isApiError } from '../api/client';
import { button, div, el, note } from './dom';

// D163 — the feedback box: one textarea for opinions / feature requests, POSTed to /feedback.
// Reachable from the home utility bar and the pause menu; guest-open, because the Instant pillar puts
// no sign-in wall in front of an opinion. The sent state is the hero (same doctrine as the sign-in
// screen): a calm confirmation view, not a tick appended to a form. All strings land via textContent
// (§15.1); the api is injected so jsdom pins the wire contract (T-ARCH-004).

export interface FeedbackApi {
  post<T>(path: string, body?: unknown): Promise<T>;
}

export interface FeedbackOpts {
  doc: Document;
  api: FeedbackApi;
  /** The display name from the identity chip — already public over the player's head in matches. */
  playerName: () => string;
}

export type FeedbackContext = 'home' | 'pause';

export interface FeedbackUi {
  readonly root: HTMLElement;
  open(context: FeedbackContext): void;
  close(): void;
}

export function mountFeedback(opts: FeedbackOpts): FeedbackUi {
  const d = opts.doc;
  const root = d.createElement('div');
  // z230: above the home screen (40), the pause menu (120) and the controls screen (220) — the box
  // can be opened over any of them.
  root.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(4,22,12,.85);' +
    'color:var(--ink-1);font-family:var(--font-ui);z-index:230;padding:24px';
  const panel = div('min-width:340px;max-width:460px;width:100%;padding:24px 28px;border:1px solid var(--border-hairline);border-radius:12px;background:var(--bg-panel)');
  root.append(panel);
  d.body.appendChild(root);

  const close = (): void => void (root.style.display = 'none');

  // Escape closes the box — and must NOT bubble to the window, where the pause handler listens.
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      close();
    }
  });

  const sentView = (): void => {
    const c = div();
    c.append(div('font-size:18px;font-weight:600;margin-bottom:6px', '✓ Sent — thanks.'));
    c.append(note('A human reads these. Every one.'));
    c.append(div('margin-top:12px'), button('Close', () => close()));
    panel.replaceChildren(c);
  };

  const formView = (context: FeedbackContext): void => {
    const c = div();
    c.append(div('font-size:18px;font-weight:600;margin-bottom:2px', 'Feedback'));
    c.append(note('Opinions, feature requests, match reports — a human reads all of it.'));
    const box = el('textarea');
    box.maxLength = 2000;
    box.placeholder = 'What should FAFI do differently?';
    box.style.cssText =
      'width:100%;min-height:120px;box-sizing:border-box;resize:vertical;margin:6px 0;padding:10px 12px;' +
      'border:1px solid var(--border-strong);background:var(--bg-keycap);color:var(--ink-1);border-radius:6px;' +
      'font-family:inherit;font-size:14px;line-height:1.45';
    const msg = note('');
    const send = button('Send', () => {
      const text = box.value.trim();
      if (!text) {
        msg.textContent = 'Write something first.';
        return;
      }
      send.disabled = true;
      msg.textContent = 'sending…';
      msg.style.color = 'var(--ink-2)';
      opts.api
        .post('/feedback', { text, name: opts.playerName().slice(0, 20), context })
        .then(() => sentView())
        .catch((e: unknown) => {
          send.disabled = false; // the text is still in the box — retry is free
          msg.style.color = 'var(--danger-ink)';
          msg.textContent = isApiError(e) ? e.message : 'network error — try again';
        });
    }, { primary: true });
    c.append(box, send, button('Cancel', () => close()), msg);
    panel.replaceChildren(c);
    box.focus();
  };

  return {
    root,
    open: (context) => {
      formView(context); // always a fresh form — a sent box never lingers into the next opinion
      root.style.display = 'flex';
    },
    close,
  };
}
